import { useEffect, useRef, useState, useCallback } from "react";

// Real-time hook for the Podcast Hub. Connects to the backend WebSocket room,
// dispatches incoming messages to onMessage, and auto-reconnects.
export function useHubSocket(sessionId, onMessage) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const closedRef = useRef(false);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const connect = useCallback(() => {
    if (!sessionId) return;
    const token = localStorage.getItem("fc_token") || "";
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    const url = `${base}/api/ws/hub/${sessionId}?token=${encodeURIComponent(token)}`;
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      return;
    }
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onmessage = (e) => {
      try {
        onMessageRef.current(JSON.parse(e.data));
      } catch (err) {
        /* ignore */
      }
    };
    ws.onclose = () => {
      setConnected(false);
      if (!closedRef.current) {
        reconnectRef.current = setTimeout(connect, 2500);
      }
    };
    ws.onerror = () => {
      try {
        ws.close();
      } catch (e) {
        /* ignore */
      }
    };
  }, [sessionId]);

  useEffect(() => {
    closedRef.current = false;
    connect();
    const hb = setInterval(() => {
      if (wsRef.current?.readyState === 1) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 25000);
    return () => {
      closedRef.current = true;
      clearInterval(hb);
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      try {
        wsRef.current?.close();
      } catch (e) {
        /* ignore */
      }
    };
  }, [connect]);

  const sendReaction = useCallback((emoji) => {
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: "reaction", emoji }));
    }
  }, []);

  const sendChat = useCallback((text) => {
    if (wsRef.current?.readyState === 1 && text && text.trim()) {
      wsRef.current.send(JSON.stringify({ type: "chat", text: text.trim() }));
      return true;
    }
    return false;
  }, []);

  const deleteChatMessage = useCallback((messageId) => {
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: "chat_delete", message_id: messageId }));
    }
  }, []);

  const timeoutUser = useCallback((userId, minutes) => {
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: "timeout", user_id: userId, minutes }));
    }
  }, []);

  const untimeoutUser = useCallback((userId) => {
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: "untimeout", user_id: userId }));
    }
  }, []);

  return { connected, sendReaction, sendChat, deleteChatMessage, timeoutUser, untimeoutUser };
}
